/**
 * Разбор НЕЗАВЕРШЁННОГО полигона редактора контура.
 *
 * Terra-draw держит рисуемую фигуру прямо в своём store и помечает её
 * свойством `currentlyDrawing`. Пока полигон не замкнут, наружу (в
 * `currentRing()`) он не отдаётся вовсе — поэтому «Готово» с тремя
 * поставленными точками уходило в ветку «геометрии нет» и молча выбрасывало
 * работу человека.
 *
 * Здесь мы достаём уже поставленные точки, чтобы «Готово» могло замкнуть
 * контур само — так делают все ГИС-редакторы.
 *
 * Функция чистая: на вход — снимок фич, на выход — кольцо или null. Никакой
 * terra-draw в тестах не нужен.
 */

export type LngLat = [number, number];

/** Свойство terra-draw на рисуемой прямо сейчас фигуре (`common.d.ts`). */
const CURRENTLY_DRAWING = 'currentlyDrawing';
/** Сколько точек человек реально поставил (без «резинки» под курсором). */
const COMMITTED_COUNT = 'committedCoordinateCount';

interface SnapshotFeature {
  geometry?: { type?: string; coordinates?: unknown };
  properties?: Record<string, unknown> | null;
}

/** Две точки в пределах ~1 см — считаем одной (дребезг клика и замыкание). */
function samePoint(a: LngLat, b: LngLat): boolean {
  return Math.abs(a[0] - b[0]) < 1e-7 && Math.abs(a[1] - b[1]) < 1e-7;
}

/**
 * Точки незавершённого полигона из снимка редактора.
 *
 * `null` — рисование не идёт либо точек меньше трёх: замыкать нечего, и
 * вызывающий обязан сказать об этом человеку, а не молча всё стереть.
 *
 * Возвращаем НЕзамкнутое кольцо (без дубля первой точки в конце) — замыкание
 * делает вызывающий, у него для этого есть проверенный путь.
 */
export function draftRingFromSnapshot(
  features: readonly SnapshotFeature[] | null | undefined,
): LngLat[] | null {
  const points = draftPointsFromSnapshot(features);
  return points && points.length >= 3 ? points : null;
}

/**
 * Поставленные точки НЕЗАВЕРШЁННОГО полигона, сколько бы их ни было.
 *
 * Отличается от `draftRingFromSnapshot` ровно тем, что не отбрасывает
 * короткие наборы: `null` здесь значит «рисование не идёт вовсе», а массив
 * из одной-двух точек — «идёт, но замыкать ещё рано». Вызывающему эти два
 * случая надо различать: человеку, который не начинал рисовать, и человеку,
 * поставившему две точки, нужно сказать разное.
 */
export function draftPointsFromSnapshot(
  features: readonly SnapshotFeature[] | null | undefined,
): LngLat[] | null {
  if (!features?.length) return null;

  const drawing = features.find((f) => f.properties?.[CURRENTLY_DRAWING] === true);
  if (!drawing || drawing.geometry?.type !== 'Polygon') return null;

  const rings = drawing.geometry.coordinates;
  if (!Array.isArray(rings) || !Array.isArray(rings[0])) return null;
  const raw = (rings[0] as unknown[]).filter(
    (p): p is LngLat =>
      Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]),
  );
  if (!raw.length) return null;

  // Последняя точка кольца — дубль первой (полигон в GeoJSON всегда замкнут),
  // а предпоследняя, пока идёт рисование, — «резинка» под курсором, которую
  // человек ещё не ставил. Обрезаем по счётчику подтверждённых точек, если
  // terra-draw его дал; иначе снимаем замыкание и дедуплицируем хвост.
  const committed = drawing.properties?.[COMMITTED_COUNT];
  let points: LngLat[] =
    typeof committed === 'number' && committed >= 3 && committed <= raw.length
      ? raw.slice(0, committed)
      : raw.slice(0, -1);

  // Дедуп подряд идущих совпадений (в т.ч. если замыкание уцелело).
  points = points.filter((p, i) => i === 0 || !samePoint(p, points[i - 1]));
  if (points.length >= 2 && samePoint(points[0], points[points.length - 1])) {
    points = points.slice(0, -1);
  }

  return points;
}
